import React, { useEffect, useState } from 'react';
import { applicationsAPI } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FileText, DollarSign, Home, Eye, Edit, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [editFormData, setEditFormData] = useState({
    borrower_name: '',
    email: '',
    phone: '',
    property_address: '',
    loan_purpose: '',
    loan_amount: '',
    property_value: '',
    credit_score: '',
    down_payment: ''
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await applicationsAPI.getAll();
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Approved': 'bg-green-100 text-green-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Processing': 'bg-blue-100 text-blue-700',
      'Under Review': 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const handleViewDetails = async (app) => {
    try {
      const response = await applicationsAPI.getOne(app.application_id);
      setSelectedApp(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  const handleEditStatus = (app) => {
    setEditingApp(app);
    setNewStatus(app.status);
    setShowStatusDialog(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await applicationsAPI.updateStatus(editingApp.application_id, newStatus);
      toast.success('Status updated successfully');
      setShowStatusDialog(false);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEditApplication = async (app) => {
    try {
      const response = await applicationsAPI.getOne(app.application_id);
      const fullApp = response.data;
      setEditingApp(fullApp);
      setEditFormData({
        borrower_name: fullApp.borrower_name || '',
        email: fullApp.email || '',
        phone: fullApp.phone || '',
        property_address: fullApp.property_address || '',
        loan_purpose: fullApp.loan_purpose || '',
        loan_amount: fullApp.loan_amount?.toString() || '',
        property_value: fullApp.property_value?.toString() || '',
        credit_score: fullApp.credit_score || '',
        down_payment: fullApp.down_payment?.toString() || ''
      });
      setShowEditDialog(true);
    } catch (error) {
      toast.error('Failed to load application');
    }
  };

  const handleSaveApplication = async () => {
    try {
      await applicationsAPI.update(editingApp.application_id, {
        borrower_name: editFormData.borrower_name,
        email: editFormData.email,
        phone: editFormData.phone,
        property_address: editFormData.property_address,
        loan_purpose: editFormData.loan_purpose,
        loan_amount: parseFloat(editFormData.loan_amount) || 0,
        property_value: parseFloat(editFormData.property_value) || 0,
        credit_score: editFormData.credit_score,
        down_payment: parseFloat(editFormData.down_payment) || null,
        application_data: editingApp.application_data
      });
      toast.success('Application updated successfully');
      setShowEditDialog(false);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to update application');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="applications-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-primary">Loan Applications</h1>
        <p className="text-muted-foreground mt-2">View and manage loan applications</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {applications.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            No applications found
          </div>
        ) : (
          applications.map((app, index) => (
            <Card key={app.application_id} className="hover:shadow-lg transition-shadow" data-testid={`application-card-${index}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{app.borrower_name}</CardTitle>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${getStatusColor(app.status)}`}>
                      {app.status}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleViewDetails(app)}
                      data-testid={`view-app-${index}`}
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEditApplication(app)}
                      data-testid={`edit-app-${index}`}
                      title="Edit Application"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEditStatus(app)}
                      data-testid={`edit-status-${index}`}
                      title="Change Status"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">${app.loan_amount.toLocaleString()}</span>
                    {app.property_value > 0 && (
                      <span className="text-muted-foreground">/ ${app.property_value.toLocaleString()}</span>
                    )}
                  </div>
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Home className="h-4 w-4 mt-0.5" />
                    <span className="line-clamp-2">{app.property_address}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium">{app.loan_purpose}</p>
                    {app.credit_score && (
                      <p className="text-sm text-muted-foreground mt-1">Credit Score: {app.credit_score}</p>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Applied: {new Date(app.created_at).toLocaleDateString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Application Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedApp.borrower_name}</DialogTitle>
                <DialogDescription>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(selectedApp.status)}`}>
                    {selectedApp.status}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Email</Label>
                    <p className="font-medium">{selectedApp.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <p className="font-medium">{selectedApp.phone}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Loan Amount</Label>
                    <p className="font-medium">${selectedApp.loan_amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Loan Purpose</Label>
                    <p className="font-medium">{selectedApp.loan_purpose}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Credit Score</Label>
                    <p className="font-medium">{selectedApp.credit_score || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Applied Date</Label>
                    <p className="font-medium">{new Date(selectedApp.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Property Address</Label>
                  <p className="font-medium">{selectedApp.property_address}</p>
                </div>

                {selectedApp.application_data && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg">Additional Information</h3>
                    {selectedApp.application_data.company && (
                      <div>
                        <Label className="text-muted-foreground">Company</Label>
                        <p>{selectedApp.application_data.company}</p>
                      </div>
                    )}
                    {selectedApp.application_data.property_info && (
                      <div>
                        <Label className="text-muted-foreground">Property Information</Label>
                        <p>{selectedApp.application_data.property_info}</p>
                      </div>
                    )}
                    {selectedApp.application_data.message && (
                      <div>
                        <Label className="text-muted-foreground">Message</Label>
                        <p className="whitespace-pre-wrap">{selectedApp.application_data.message}</p>
                      </div>
                    )}
                    {selectedApp.application_data.investment_areas?.length > 0 && (
                      <div>
                        <Label className="text-muted-foreground">Investment Areas</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedApp.application_data.investment_areas.map((area, i) => (
                            <span key={i} className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Application Status</DialogTitle>
            <DialogDescription>
              Change the status of {editingApp?.borrower_name}'s application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Processing">Processing</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateStatus}>
                Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Application Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>Update application details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_borrower_name">Borrower Name *</Label>
                <Input
                  id="edit_borrower_name"
                  value={editFormData.borrower_name}
                  onChange={(e) => setEditFormData({ ...editFormData, borrower_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email *</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone *</Label>
                <Input
                  id="edit_phone"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_loan_purpose">Loan Purpose *</Label>
                <Select 
                  value={editFormData.loan_purpose} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, loan_purpose: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Purchase</SelectItem>
                    <SelectItem value="refinance">Refinance</SelectItem>
                    <SelectItem value="cash-out">Cash-Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_loan_amount">Loan Amount *</Label>
                <Input
                  id="edit_loan_amount"
                  type="number"
                  value={editFormData.loan_amount}
                  onChange={(e) => setEditFormData({ ...editFormData, loan_amount: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_property_value">Property Value</Label>
                <Input
                  id="edit_property_value"
                  type="number"
                  value={editFormData.property_value}
                  onChange={(e) => setEditFormData({ ...editFormData, property_value: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_credit_score">Credit Score</Label>
                <Input
                  id="edit_credit_score"
                  value={editFormData.credit_score}
                  onChange={(e) => setEditFormData({ ...editFormData, credit_score: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_down_payment">Down Payment</Label>
                <Input
                  id="edit_down_payment"
                  type="number"
                  value={editFormData.down_payment}
                  onChange={(e) => setEditFormData({ ...editFormData, down_payment: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_property_address">Property Address *</Label>
              <Textarea
                id="edit_property_address"
                value={editFormData.property_address}
                onChange={(e) => setEditFormData({ ...editFormData, property_address: e.target.value })}
                rows={2}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveApplication}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Applications;
